import React, { useRef, useMemo, memo } from 'react';
import { Loader } from 'lucide-react';
import useOverlayScrollBlocks from './useOverlayScrollBlocks';
import { isTauri } from '../../utils/tauri';
import { markdownToHtml } from '../../utils/markdownToHtml';
import { sanitizeRichTextHtml } from '../../utils/sanitizeHtml';
import { ToolCallCard, TypingIndicator } from './ToolCallCard';

// Anchor clicks inside sanitized assistant markdown, delegated (the HTML is
// dangerouslySetInnerHTML, so elements can't carry React handlers). Links are
// LIVE now that sanitizeRichTextHtml keeps https hrefs ('a' was allowlisted
// for page-link chips): without this, a click would navigate the app's own
// webview to the target site. Route through the OS browser instead — same
// pattern as ChatMessageView's openExternalUrl. The sanitizer only lets
// http(s) hrefs through, and the scheme is re-checked here anyway.
export function onAiMarkdownClick(e) {
  const anchor = e.target?.closest?.('a[href]');
  if (!anchor) return;
  e.preventDefault();
  const href = anchor.getAttribute('href') || '';
  if (!/^https?:\/\//i.test(href)) return;
  if (isTauri()) {
    import('@tauri-apps/plugin-shell')
      .then(({ open }) => open(href))
      .catch(() => window.open(href, '_blank', 'noopener,noreferrer'));
  } else {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
}

export const MessageBubble = memo(function MessageBubble({ message, onToolConfirm }) {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming;
  const hasToolCalls = (message.toolCalls || []).length > 0;

  // Assistant content is markdown → sanitized HTML (recomputed as tokens stream).
  const renderedHtml = useMemo(
    () => (message.content ? sanitizeRichTextHtml(markdownToHtml(message.content)) : ''),
    [message.content]
  );
  // Code blocks / tables scroll with the app's scrollbar, not the native one.
  const markdownRef = useRef(null);
  useOverlayScrollBlocks(markdownRef, renderedHtml, !isUser && !isStreaming);

  if (isUser) {
    return (
      <div className="ai-chat-message ai-chat-message--user">
        <div className="ai-chat-user-bubble">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="ai-chat-message ai-chat-message--assistant">
      <div className="ai-chat-assistant-content w-full min-w-0 text-sm text-gray-900 dark:text-zinc-100">
          {(message.content || isStreaming) && (
            <div className="ai-chat-assistant-text min-h-[1.25rem]" style={{ wordBreak: 'break-word' }}>
              {message.content && (
                <div
                  ref={markdownRef}
                  className="ai-chat-markdown"
                  // Assistant text is markdown (bold, lists, code). Render it so
                  // **bold** shows as bold rather than literal asterisks; sanitized
                  // to strip any unsafe HTML the model might emit.
                  dangerouslySetInnerHTML={{ __html: renderedHtml }}
                  onClick={onAiMarkdownClick}
                />
              )}
              {isStreaming && !message.content && !hasToolCalls && <TypingIndicator />}
              {isStreaming && message.content && <TypingIndicator />}
              {isStreaming && !message.content && hasToolCalls && (
                <span className="inline-flex items-center gap-2 text-gray-500 dark:text-zinc-400 text-xs">
                  <Loader size={12} className="animate-spin flex-shrink-0" />
                  Running tools…
                </span>
              )}
            </div>
          )}
          {(message.toolCalls || []).length > 0 && (
            <div className="ai-chat-tool-list w-full min-w-0">
              {(message.toolCalls || []).map((tc, i) => (
                <ToolCallCard
                  key={i}
                  toolName={tc.name}
                  toolInput={tc.input}
                  toolResult={tc.result}
                  interactive={tc.interactive}
                  awaitingConfirm={tc.awaitingConfirm}
                  onConfirm={onToolConfirm}
                />
              ))}
            </div>
          )}
        </div>
    </div>
  );
});
